/**
 * P385.4 -- hop-by-hop trace reveal.
 *
 * A trace propagates: light the seed's direct neighbours, then trace to
 * the next hop, and so on. That reads as propagation, where the P91
 * travelling orbs (all edges animating at once) read as traffic.
 *
 * The timing rules exist because an operations wall has two audiences
 * with opposite needs. A spine blast reaches ~59 nodes; a fixed 1.5s
 * per hop would be 8+ seconds before the picture is complete --
 * compelling in a demo, punishing for an operator asking "what is
 * broken NOW". So the reveal is bounded, skippable, and settles to a
 * static lit state rather than looping.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/** Hard ceiling on a full reveal, however deep the graph. */
export const MAX_REVEAL_MS = 4000;
/** Longest a single step may take (shallow traces still feel deliberate). */
export const MAX_HOP_MS = 700;
/** Shortest step that still reads as a step rather than a flicker. */
export const MIN_HOP_MS = 120;

export interface RevealPlan {
  /** Number of animation steps. */
  steps: number;
  /** Milliseconds per step. */
  stepMs: number;
  /** Hop levels lit per step -- >1 on a deep trace. */
  hopsPerStep: number;
}

/**
 * Plan a reveal for a trace `depth` hops deep.
 *
 * Two properties are in tension on a deep graph: every step should be
 * perceptible (>= MIN_HOP_MS) and the whole reveal should finish within
 * MAX_REVEAL_MS. A spine blast reaches ~59 hops, where one-hop-per-step
 * at the floor would take ~7s.
 *
 * Resolved by lighting MULTIPLE hop levels per step once the trace is
 * deeper than the budget allows, rather than shortening steps into a
 * flicker. Both properties then hold at any depth.
 */
export function revealPlan(depth: number): RevealPlan {
  if (depth <= 0) return { steps: 0, stepMs: 0, hopsPerStep: 0 };
  const maxSteps = Math.max(1, Math.floor(MAX_REVEAL_MS / MIN_HOP_MS));
  const steps = Math.min(depth, maxSteps);
  const hopsPerStep = Math.ceil(depth / steps);
  const stepMs = Math.min(MAX_HOP_MS, Math.floor(MAX_REVEAL_MS / steps));
  return { steps, stepMs, hopsPerStep };
}

/** Per-step duration. Kept as a named helper for readability. */
export function hopDurationMs(depth: number): number {
  return revealPlan(depth).stepMs;
}

/** True when the viewer asked for reduced motion. Defensive: jsdom and
 * older browsers may not implement matchMedia. */
export function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  } catch {
    return false;
  }
}

export interface HopReveal {
  /** Hops currently lit. Infinity once fully revealed, so callers can
   * compare `nodeDepth <= revealDepth` without special-casing. */
  revealDepth: number;
  /** Jump straight to the final frame. */
  skip: () => void;
  revealing: boolean;
}

/**
 * Drive the reveal for a trace `depth` hops deep, restarting whenever
 * `key` changes (a new seed, or a direction flip).
 */
export function useHopReveal(depth: number, key: string | null): HopReveal {
  const [revealDepth, setRevealDepth] = useState<number>(Infinity);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const skip = useCallback(() => {
    stop();
    setRevealDepth(Infinity);
  }, [stop]);

  useEffect(() => {
    stop();
    // Nothing selected, nothing reached, or motion is unwelcome: show
    // the finished picture immediately.
    if (key === null || depth <= 0 || prefersReducedMotion()) {
      setRevealDepth(Infinity);
      return;
    }

    setRevealDepth(0);
    const { stepMs, hopsPerStep } = revealPlan(depth);
    timer.current = setInterval(() => {
      setRevealDepth((cur) => {
        const next = cur + hopsPerStep;
        if (next >= depth) {
          stop();
          // Settle to a STATIC lit state rather than looping.
          return Infinity;
        }
        return next;
      });
    }, stepMs);

    return stop;
  }, [depth, key, stop]);

  const revealing = useMemo(() => Number.isFinite(revealDepth), [revealDepth]);
  return { revealDepth, skip, revealing };
}
